import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  createContext
} from 'react'
import propTypes from 'prop-types'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import standardStyle from 'assets/css/standardStyle'
import { Table, Modal } from 'ui'
import useGetReservationsById from 'hooks/useGetReservationsById'
import useGetUsers from 'hooks/useGetUsers'
import { formatDate } from 'utils/dateUtils'
import { ReactComponent as SorterIcon } from 'assets/icons/sorter.svg'
import { useGetAPI } from 'hooks/useGenericAPIs'
import useDeleteReservationsById from 'hooks/useDeleteReservationsById'
import { toast } from 'react-hot-toast'

// @ts-ignore
const LoadingContext = createContext()

export const offerStatus = {
  complete: { text: 'Complete', backgroundColor: 'bg-green-700' },
  pending: { text: 'Pending', backgroundColor: 'bg-gray-600' },
  cancelled: { text: 'Cancelled', backgroundColor: 'bg-red-600' }
}

const ColoredStatus = ({ status }) => (
  <div
    className={`font-medium py-1 text-base text-white uppercase
      ${
        offerStatus[status.toLowerCase()].backgroundColor || 'bg-gray-600'
      } text-center
    `}
  >
    {offerStatus[status.toLowerCase()].text}
  </div>
)

const ActionButton = ({ text, onClick, value }) => {
  return (
    <div
      className='font-medium py-1 text-base text-black uppercase text-center border border-black w-32 cursor-pointer'
      onClick={onClick}
    >
      View/Edit
    </div>
  )
}

const DeleteButton = ({ onClick }) => {
  return null
  // return (
  //   <div
  //     className='font-medium py-1 text-base text-black uppercase text-center border border-black w-32 cursor-pointer bg-red-600'
  //     onClick={onClick}
  //   >
  //     Delete
  //   </div>
  // )
}

const TextField = ({ value }) => (
  <span className='font-medium text-base text-softBlack capitalize'>
    {value}
  </span>
)

const MemoTable = ({
  dataSource,
  columns,
  loading,
  idBeingDeleted,
  deleting
}) => {
  return (
    <Table
      className='rounded-lg'
      dataSource={dataSource}
      columns={columns}
      tailwindTable
      loading={loading}
      idBeingDeleted={idBeingDeleted}
      deleting={deleting}
    />
  )
}

const CardComponent = ({ title, count }) => (
  <div
    className='bg-white rounded col-span-1'
    style={{ fontFamily: standardStyle.fonts.sfpro }}
  >
    <div className='flex flex-col space-y-2 p-4'>
      <div className='text-xs text-softBlack_70 font-medium uppercase'>
        {title}
      </div>
      <div className='text-sm font-bold text-softBlack'>{count}</div>
    </div>
  </div>
)

const ProjectReservations = ({ appProject, userObject }) => {
  const [tableDataSource, setTableDataSource] = useState([])
  const [filteredTableDataSource, setFilteredTableDataSource] = useState([])
  const [idBeingDeleted] = useState('')
  const [deletingReservation] = useState(false)
  const {
    reservations,
    loading,
    refetch: refetchReservations
  } = useGetReservationsById()
  const { users, isLoading } = useGetUsers()
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(-1)

  const history = useHistory()

  const [sortType, setSortType] = useState('asc')
  const [sortByField, setSortByField] = useState('createdAt')
  const { deleteAPI: deleteReservationById, loading: isDeleteInProgress } =
    useDeleteReservationsById()
  const { data: fetchedDataByReservationType, fetch: fetchByReservationType } =
    useGetAPI()
  const { data: fetchedDataByPaymentStatus, fetch: fetchByPaymentStatus } =
    useGetAPI()
  const { data: fetchedDataBySignatureStatus, fetch: fetchBySignatureStatus } =
    useGetAPI()

  useEffect(() => {
    fetchByReservationType(
      '/analytics/reservation/metrics/groupBy/reservationStatus'
    )
    fetchByPaymentStatus('/analytics/reservation/metrics/groupBy/paymentStatus')
    fetchBySignatureStatus(
      '/analytics/reservation/metrics/groupBy/primaryBuyerSignatureStatus'
    )
  }, [])

  useEffect(() => {
    if (reservations && users) {
      const updatedReservations = reservations.map((reservation) => {
        const user = users?.find?.(
          (usr) => usr?._id === reservation?.primaryBuyer
        )
        const firstName = user?.firstName
        const lastName = user?.lastName
        return {
          ...reservation,
          reservation_holder:
            firstName && lastName ? firstName + ' ' + lastName : null
        }
      })
      setTableDataSource(updatedReservations)
    }
  }, [reservations, loading, users])

  const onSortClicked = useCallback(
    (field) => {
      let newSortType = 'asc'
      if (field === sortByField) {
        newSortType = sortType === 'asc' ? 'desc' : 'asc'
      }
      const sortedData = tableDataSource.sort((a, b) => {
        if (newSortType === 'asc') {
          return a[field]?.localeCompare(b[field])
        } else if (newSortType === 'desc') {
          return b[field]?.localeCompare(a[field])
        } else return 0
      })
      setFilteredTableDataSource([...sortedData])
      setSortByField(field)
      setSortType(newSortType)
    },
    [tableDataSource, sortByField, sortType]
  )

  const tableColumns = useMemo(
    () => [
      {
        Title: (
          <span className='flex items-center space-x-2'>
            <span className='leading-none'>Date Created</span>
            <button onClick={() => onSortClicked('createdAt')}>
              <SorterIcon />
            </button>
          </span>
        ),
        accessor: 'createdAt',
        dataIndex: 'createdAt',
        Cell: (props) => <TextField value={formatDate(props.cell.value)} />
      },
      {
        Title: (
          <span className='flex items-center space-x-2'>
            <span className='leading-none'>Reservation Holder(s)</span>
            <button onClick={() => onSortClicked('reservation_holder')}>
              <SorterIcon />
            </button>
          </span>
        ),
        accessor: 'reservation_holder',
        Cell: (props) => <TextField value={props.cell.value} />
      },
      {
        Title: (
          <span className='flex items-center space-x-2'>
            <span className='leading-none'>Signature Status</span>
            <button
              onClick={() => onSortClicked('primaryBuyerSignatureStatus')}
            >
              <SorterIcon />
            </button>
          </span>
        ),
        accessor: 'primaryBuyerSignatureStatus',
        Cell: (props) => <TextField value={props.cell.value} />
      },
      {
        Title: (
          <span className='flex items-center space-x-2'>
            <span className='leading-none'>Payment Status</span>
            <button onClick={() => onSortClicked('paymentStatus')}>
              <SorterIcon />
            </button>
          </span>
        ),
        accessor: 'paymentStatus',
        Cell: (props) => <TextField value={props.cell.value} />
      },
      {
        Title: (
          <span className='flex items-center space-x-2'>
            <span className='leading-none'>Reservation Status</span>
            <button onClick={() => onSortClicked('reservationStatus')}>
              <SorterIcon />
            </button>
          </span>
        ),
        accessor: 'reservationStatus',
        Cell: (props) => <ColoredStatus status={props.cell.value} />
      },
      {
        Title: '',
        accessor: '_id',
        Cell: (props) => (
          <div className='flex flex-row gap-2'>
            <ActionButton
              text={props.cell.value}
              value={props.cell}
              onClick={() =>
                history.push(`/admin/reservations/${props.cell.value}`)
              }
            />
            <DeleteButton
              onClick={() => {
                console.log('DeleteButton clicked', props.cell.value)
                setIsDeleteDialogOpened(props.cell.value)
              }}
            />
          </div>
        )
      }
    ],
    [onSortClicked]
  )

  const isDataLoading = useMemo(
    () => loading || isLoading || isDeleteInProgress,
    [loading, isLoading, isDeleteInProgress]
  )

  const values = useMemo(() => ({}), [])
  const tabeleDataSource = useMemo(() => {
    const values =
      filteredTableDataSource?.length > 0
        ? filteredTableDataSource
        : tableDataSource
    return values.filter((x) => !x.deleted)
  }, [filteredTableDataSource, tableDataSource])

  const deleteSelectedReservation = useCallback(async () => {
    const toastId = toast.loading('Deleting reservation...')
    try {
      const reservationId = isDeleteDialogOpened
      setIsDeleteDialogOpened(-1)
      await deleteReservationById(reservationId)
      refetchReservations()
      toast.dismiss(toastId)
      toast.success('Deleted reservation successfully')
    } catch (err) {
      toast.dismiss(toastId)
      toast.error('Failed to delete reservation')
    }
  }, [isDeleteDialogOpened, refetchReservations])

  return (
    <LoadingContext.Provider value={values}>
      <div className='h-full w-full flex flex-col items-center'>
        <div className='w-full lg:overflow-x-hidden'>
          <div className='mt-5 grid grid-flow-col grid-cols-3 sm:grid-cols-7 gap-2'>
            {fetchedDataByReservationType
              ?.filter((item) => item._id?.length)
              ?.map((item) => {
                return (
                  <CardComponent
                    title={`Reservation ${item._id}`}
                    count={item.count}
                  />
                )
              })}
            {fetchedDataByPaymentStatus
              ?.filter((item) => item._id?.length)
              ?.map((item) => {
                return (
                  <CardComponent
                    title={`Payment Status - ${item._id}`}
                    count={item.count}
                  />
                )
              })}
            {fetchedDataBySignatureStatus
              ?.filter((item) => item._id?.length)
              ?.map((item) => {
                return (
                  <CardComponent
                    title={`Signature Status - ${item._id}`}
                    count={item.count}
                  />
                )
              })}
          </div>
          <div className='mt-5'>
            {isDataLoading && (
              <div className='flex w-full justify-center items-center'>
                loading reservations....
              </div>
            )}
            {!isDataLoading && (
              <MemoTable
                dataSource={tabeleDataSource}
                columns={tableColumns}
                loading={loading}
                idBeingDeleted={idBeingDeleted}
                deleting={deletingReservation}
              />
            )}
            <Modal
              showModal={isDeleteDialogOpened !== -1}
              onCancel={() => setIsDeleteDialogOpened(-1)}
              modalType='alert'
              title='Are you sure?'
              subtitle='Do you want to delete reservation?'
              onOk={deleteSelectedReservation}
            />
          </div>
        </div>
      </div>
    </LoadingContext.Provider>
  )
}

const mapStateToProps = (state) => ({
  appProject: state.appReducer.appProject,
  userObject: state.authReducer.userObject
})

const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectReservations)

ProjectReservations.propTypes = {
  appProject: propTypes.string,
  userObject: propTypes.shape({
    userType: propTypes.string
  }),
  cell: propTypes.shape({
    value: propTypes.string
  }),
  row: propTypes.shape({
    original: propTypes.shape({
      _id: propTypes.string
    })
  })
}
